import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {AdminUser} from '~/modules/api';

import {RootState} from '../rootReducer';

export type UserDetails = {
  userId?: string;
  userEmail?: string | null;
  idToken?: string;
  adminUser?: AdminUser | null;
  pin?: {lastVerified: string} | null;
};

export type PendingActionType = 'runDatoSourceReferencesExtract' | 'runExtract';

export type PendingAction = {
  id: string;
  type: PendingActionType;
  timestamp: number;
};

export type AuthState = UserDetails & {
  authStateKnown: boolean;
  showPinConfirm: boolean;
  pendingAction: PendingAction | null;
  lastPinVerification: number | null;
};

export const initialUserDetails: UserDetails = {
  userId: undefined,
  userEmail: undefined,
  idToken: undefined,
};

const initialAuthState: AuthState = {
  ...initialUserDetails,
  adminUser: undefined,
  pin: undefined,
  showPinConfirm: false,
  authStateKnown: false,
  pendingAction: null,
  lastPinVerification: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    onSetAuthState(state, action: PayloadAction<UserDetails>) {
      state.authStateKnown = true;
      state.userId = action.payload.userId;
      state.userEmail = action.payload.userEmail;
      state.idToken = action.payload.idToken;
      if (typeof action.payload.adminUser !== 'undefined') {
        state.adminUser = action.payload.adminUser;
      }
    },
    onSetAdminUser(
      state,
      action: PayloadAction<{
        user: AdminUser | null;
      }>,
    ) {
      state.adminUser = action.payload.user;
    },
    onSetAdminPin(
      state,
      action: PayloadAction<{
        pin: {lastVerified: string} | null;
      }>,
    ) {
      state.pin = action.payload.pin;
    },
    onSetShowPinConfirm(
      state,
      action: PayloadAction<{showPinConfirm: boolean}>,
    ) {
      state.showPinConfirm = action.payload.showPinConfirm;
    },
    addPendingAction(
      state,
      action: PayloadAction<{type: PendingActionType}>,
    ) {
      const id = `action_${Date.now()}_${Math.random()}`;
      state.pendingAction = {
        id,
        type: action.payload.type,
        timestamp: Date.now(),
      };
    },
    clearPendingAction(state) {
      state.pendingAction = null;
    },
    setLastPinVerification(state) {
      state.lastPinVerification = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isLogoutAction, (state) => {
      // Clear pin verification when user logs out
      state.lastPinVerification = null;
      state.pendingAction = null;
      state.showPinConfirm = false;
    });
  },
});

export const authReducer = authSlice.reducer;
export const authActions = authSlice.actions;

export const authSelectors = {
  getUserId: (state: RootState) => state.auth.userId,
  getUserIdToken: (state: RootState) => state.auth.idToken,
  getShowPinConfirm: (state: RootState) => state.auth.showPinConfirm,
  getPendingAction: (state: RootState) => state.auth.pendingAction,
  getLastPinVerification: (state: RootState) => state.auth.lastPinVerification,
};

export function isLogoutAction(action: any) {
  return (
    action?.type === authSlice.actions.onSetAuthState.type &&
    action?.payload?.userId === undefined
  );
}
